import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import nobracLogo from '../assets/nobrac.jpg';
import backgroundImage from '../assets/bg.jpg';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('admin');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(username)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    if (!username || !password) {
      setError('Please enter both email and password');
      setIsLoading(false);
      return;
    }

    try {
      console.log('Submitting login with:', { email: username, password, role });
      const result = await login(username, password, role);
      console.log('Login result:', result);

      if (result.success) {
        // Use the dashboard URL from the response
        if (result.dashboardUrl) {
          console.log('Redirecting to:', result.dashboardUrl);
          navigate(result.dashboardUrl);
        } else {
          // Fallback to role-based navigation
          if (role === 'admin') {
            console.log('Redirecting to admin dashboard');
            navigate('/admin/dashboard');
          } else if (role === 'company') {
            console.log('Redirecting to company profile');
            navigate('/company/profile');
          } else if (role === 'volunteer') {
            console.log('Redirecting to volunteer profile');
            navigate('/volunteer/profile');
          }
        }
      } else {
        setError(result.error || 'Invalid credentials or role mismatch');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'An error occurred during login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // For demo purposes - quick login buttons
  const handleQuickLogin = (userRole) => {
    if (userRole === 'admin') {
      setUsername('admin@example.com');
      setPassword('admin123');
      setRole('admin');
    } else {
      setUsername('company@example.com');
      setPassword('company123');
      setRole('company');
    }
  };

  return (
    <div style={styles.container}>
      <div style={{
        ...styles.loginWrapper,
        flexDirection: isMobile ? 'column' : 'row',
        maxHeight: isMobile ? 'none' : '600px',
        margin: isMobile ? '1rem auto' : '2rem auto',
      }}>
        {/* Banner section - conditionally rendered based on screen size */}
        <div style={{
          ...styles.loginBanner,
          flex: isMobile ? '1 1 auto' : '1 1 50%',
          padding: isMobile ? '2rem 1.5rem' : '3rem 2rem',
          display: isMobile ? 'none' : 'flex', // Hide on mobile for compact view
        }}>
          {/* Banner overlay for better text readability */}
          <div style={styles.bannerOverlay}></div>

          <div style={styles.bannerContent}>
            <div style={styles.logoContainer}>
              <img
                src={nobracLogo}
                alt="NOBRAC Logo"
                style={styles.logoImage}
              />
            </div>
            <div style={styles.bannerText}>
              <h2 style={styles.bannerTitle}>Welcome to NOBRAC Portal</h2>
              <p style={styles.bannerDescription}>
                Connect farmers with resources, track agricultural data, and empower rural communities.
              </p>
              <div style={styles.bannerFeatures}>
                <div style={styles.featureItem}>
                  <span style={styles.featureIcon}>🌱</span>
                  <span style={styles.featureText}>Farmer Registration</span>
                </div>
                <div style={styles.featureItem}>
                  <span style={styles.featureIcon}>🏢</span>
                  <span style={styles.featureText}>Company Partnerships</span>
                </div>
                <div style={styles.featureItem}>
                  <span style={styles.featureIcon}>🔗</span>
                  <span style={styles.featureText}>Resource Mapping</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Login box */}
        <div style={{
          ...styles.loginBox,
          flex: isMobile ? '1 1 auto' : '1 1 50%',
          padding: isMobile ? '2rem 1.5rem' : '3rem 2rem',
        }}>
          {/* Mobile-only logo */}
          {isMobile && (
            <div style={styles.mobileLogoContainer}>
              <img
                src={nobracLogo}
                alt="NOBRAC Logo"
                style={styles.mobileLogoImage}
              />
            </div>
          )}

          <div style={styles.loginHeader}>
            <h2 style={{
              ...styles.title,
              fontSize: isMobile ? '1.5rem' : '1.8rem',
            }}>Sign In</h2>
            <p style={styles.subtitle}>Access your NOBRAC account</p>
          </div>

          {error && <div style={styles.error}>
            <span style={styles.errorIcon}>⚠️</span>
            <span>{error}</span>
          </div>}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGroup}>
              <label htmlFor="email" style={styles.label}>Email Address</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputIcon}>📧</span>
                <input
                  type="email"
                  id="email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  style={styles.input}
                  placeholder="Enter your email address"
                  autoComplete="email"
                  required
                  pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="password" style={styles.label}>Password</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputIcon}>🔒</span>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={styles.input}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Login As</label>
              <div style={{
                ...styles.roleSelector,
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '0.75rem' : '1rem',
              }}>
                <div
                  style={{
                    ...styles.roleOption,
                    ...(role === 'admin' ? styles.roleOptionActive : {}),
                    padding: isMobile ? '0.75rem' : '1rem',
                    flexDirection: isMobile ? 'row' : 'column',
                    justifyContent: isMobile ? 'flex-start' : 'center',
                  }}
                  onClick={() => setRole('admin')}
                >
                  <span style={{
                    ...styles.roleIcon,
                    marginRight: isMobile ? '0.75rem' : '0',
                    marginBottom: isMobile ? '0' : '0.5rem',
                  }}>👨‍💼</span>
                  <span style={styles.roleText}>Administrator</span>
                </div>
                <div
                  style={{
                    ...styles.roleOption,
                    ...(role === 'company' ? styles.roleOptionActive : {}),
                    padding: isMobile ? '0.75rem' : '1rem',
                    flexDirection: isMobile ? 'row' : 'column',
                    justifyContent: isMobile ? 'flex-start' : 'center',
                  }}
                  onClick={() => setRole('company')}
                >
                  <span style={{
                    ...styles.roleIcon,
                    marginRight: isMobile ? '0.75rem' : '0',
                    marginBottom: isMobile ? '0' : '0.5rem',
                  }}>🏢</span>
                  <span style={styles.roleText}>Company</span>
                </div>
              </div>
            </div>

            <button
              type="submit"
              style={{
                ...styles.button,
                padding: isMobile ? '0.85rem' : '1rem',
                fontSize: isMobile ? '0.95rem' : '1rem',
              }}
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    padding: 0,
    margin: 0,
    overflowY: 'auto',
  },
  loginWrapper: {
    display: 'flex',
    width: '900px',
    maxWidth: '95%',
    height: 'auto',
    minHeight: '600px',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
    margin: '2rem auto',
    background: 'rgba(255, 255, 255, 0.9)', // Semi-transparent white background
    backdropFilter: 'blur(10px)', // Blur effect for modern browsers
  },
  // Mobile-specific logo container
  mobileLogoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1.5rem',
  },
  mobileLogoImage: {
    height: '60px',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  },
  loginBanner: {
    flex: '1 1 50%',
    backgroundColor: 'rgba(39, 174, 96, 0.85)', // Semi-transparent primary color
    color: 'white',
    padding: '3rem 2rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundImage: 'linear-gradient(135deg, rgba(27, 94, 32, 0.9) 0%, rgba(46, 125, 50, 0.85) 100%)',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: 'inset -5px 0 15px rgba(0, 0, 0, 0.2)',
  },
  bannerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.2)', // Dark overlay for better text readability
    zIndex: 1,
  },
  bannerContent: {
    position: 'relative',
    zIndex: 2,
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '2rem',
    justifyContent: 'center',
  },
  logoImage: {
    height: '80px',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  },
  bannerText: {
    maxWidth: '400px',
  },
  bannerTitle: {
    fontSize: '1.8rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  bannerDescription: {
    fontSize: '1rem',
    lineHeight: '1.6',
    marginBottom: '2rem',
    opacity: 0.9,
  },
  bannerFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
  },
  featureIcon: {
    fontSize: '1.2rem',
    marginRight: '1rem',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  featureText: {
    fontSize: '1rem',
    fontWeight: '500',
  },
  loginBox: {
    flex: '1 1 50%',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: '3rem 2rem',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'inset 5px 0 15px rgba(0, 0, 0, 0.05)',
    justifyContent: 'center',
  },
  loginHeader: {
    marginBottom: '1.5rem',
    textAlign: 'center',
  },
  title: {
    fontSize: '1.8rem',
    color: 'var(--text-color)',
    margin: '0 0 0.5rem 0',
    fontWeight: 'bold',
  },
  subtitle: {
    fontSize: '1rem',
    color: 'var(--text-light)',
    margin: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    width: '100%',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '0.5rem',
  },
  label: {
    fontSize: '0.9rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    color: 'var(--text-color)',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  inputIcon: {
    position: 'absolute',
    left: '1rem',
    fontSize: '1rem',
    color: 'var(--text-light)',
    pointerEvents: 'none', // Ensure icon doesn't interfere with input
  },
  input: {
    width: '100%',
    padding: '0.85rem 1rem 0.85rem 2.5rem',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    fontSize: '16px', // 16px prevents zoom on iOS
    transition: 'border-color 0.3s, box-shadow 0.3s',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    height: '44px', // Minimum touch target size
    '&:focus': {
      borderColor: 'var(--primary-color)',
      boxShadow: '0 0 0 3px rgba(46, 125, 50, 0.2)',
      outline: 'none',
    },
  },
  roleSelector: {
    display: 'flex',
    gap: '1rem',
    width: '100%',
  },
  roleOption: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '1rem',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    cursor: 'pointer',
    transition: 'all 0.3s',
    minHeight: '44px', // Minimum touch target size
  },
  roleOptionActive: {
    borderColor: 'var(--primary-color)',
    backgroundColor: 'rgba(46, 125, 50, 0.05)',
    boxShadow: '0 0 0 2px rgba(46, 125, 50, 0.2)',
  },
  roleIcon: {
    fontSize: '1.5rem',
    marginBottom: '0.5rem',
  },
  roleText: {
    fontSize: '0.9rem',
    fontWeight: '500',
  },
  button: {
    padding: '1rem',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s',
    marginTop: '1rem',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    minHeight: '44px', // Minimum touch target size
    width: '100%',
    '&:hover': {
      backgroundColor: 'var(--primary-dark)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.3)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },
  },
  error: {
    backgroundColor: 'rgba(231, 76, 60, 0.1)',
    color: '#e74c3c',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    border: '1px solid rgba(231, 76, 60, 0.3)',
    fontSize: '0.9rem',
  },
  errorIcon: {
    marginRight: '0.75rem',
    fontSize: '1.1rem',
  },

};

export default LoginPage;
